// HoldTools.tsx — the "Hold" page (NEW_UI): a single screen listing the boundary
// & urge tools (all unlocked) with a header explaining how they relate to holding
// boundaries and communicating with an ex — and with anyone else in your life.
// Tapping a tool opens the real ExerciseViewer (via onOpenTool → App's overlay).
import { toolById } from '@/data/toolkit';
import { Icon, type IconName } from '@/components/icons';
import { C, SP, R, F, SIZE, W, eyebrow, HOLD_TOOL_IDS } from './theme';

const ICONS: Record<string, IconName> = {
  beaturge: 'gauge',
  boundary: 'shield',
  communicate: 'voice',
  escalation: 'refresh',
};

export function HoldTools({ onOpenTool, onClose }: { onOpenTool: (toolId: string) => void; onClose: () => void }) {
  const tools = HOLD_TOOL_IDS.map((id) => toolById(id)).filter(Boolean) as NonNullable<ReturnType<typeof toolById>>[];
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 80, background: C.page, overflowY: 'auto', WebkitOverflowScrolling: 'touch', animation: 'rise .42s cubic-bezier(.2,.7,.3,1)' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: 'calc(var(--safe-top) + 16px) 20px calc(var(--safe-bottom) + 40px)' }}>
        <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', ...eyebrow, color: C.brandDark, padding: 0, marginBottom: SP.md }}>‹ Today</button>

        <div style={{ ...eyebrow, color: C.textFaint }}>Boundaries &amp; holding the line</div>
        <h1 style={{ fontFamily: F.title, fontSize: SIZE.h1, fontWeight: W.regular, lineHeight: 1.12, letterSpacing: '-0.5px', color: C.text, margin: '6px 0 0' }}>
          Hold your <em style={{ fontStyle: 'italic', fontWeight: W.medium }}>boundary.</em>
        </h1>
        <p style={{ fontFamily: F.ui, fontSize: SIZE.md, color: C.textBody, lineHeight: 1.55, margin: '12px 0 0' }}>
          Boundaries are how you protect your recovery, with an ex who tests them and with anyone else in your life.
          These tools help you ride out the urge to reach out, decide the line you’ll hold, say it clearly, and stay
          steady when it’s pushed. Use them in any order. They’re all open to you.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: SP.md, marginTop: SP.xl }}>
          {tools.map((t) => (
            <button key={t.id} onClick={() => onOpenTool(t.id)} style={{ width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: SP.md, background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, padding: '14px 15px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <span style={{ width: 40, height: 40, borderRadius: R.md, background: 'rgba(63,167,163,0.12)', color: C.brandDark, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name={ICONS[t.id] || 'tool'} size={20} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontFamily: F.title, fontSize: SIZE.h3, fontWeight: W.medium, color: C.text, lineHeight: 1.2 }}>{t.title}</span>
                <span style={{ display: 'block', fontFamily: F.ui, fontSize: SIZE.caption, color: C.textMuted, marginTop: 2, lineHeight: 1.4 }}>{t.blurb}</span>
              </span>
              <span aria-hidden style={{ color: C.textFaint, flexShrink: 0 }}><Icon name="chevRight" size={16} /></span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
